'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import NavMarker from 'react-rhumb/NavMarker';
import { useDispatch, useSelector } from 'react-redux';
import Immutable from 'immutable';
import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import unescapedText from 'I18n/utils/unescapedText';
import MetaDescription from 'ecosystem-marketplace-core/components/elements/MetaDescription';
import MetaOpenGraph from 'ecosystem-marketplace-core/components/elements/MetaOpenGraph';
import MetaRobots from 'ecosystem-marketplace-core/components/elements/MetaRobots';
import MetaTwitter from 'ecosystem-marketplace-core/components/elements/MetaTwitter';
import DocumentHead from 'ecosystem-marketplace-core/components/portals/DocumentHead';
import PrerenderReady from 'ecosystem-marketplace-core/components/prerender/PrerenderReady';
import { UNINITIALIZED, SUCCEEDED, PENDING, FAILED } from 'ecosystem-marketplace-core/constants/RequestState';
import * as SuperCategory from 'ecosystem-marketplace-core/constants/groupTags/SuperCategory';
import { getCategoryI18nKey, getSuperCategoryI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { setDetailsReferrer } from '../../actions/DetailsActions';
import * as ecosystemClient from '../../clients/ecosystemClient';
import { DEFAULT_SEARCH_LENGTH } from '../../constants';
import * as FilterKey from '../../constants/search/FilterKey';
import * as SortField from '../../constants/search/SortField';
import SearchResult from '../../records/SearchResult';
import { PublicOnly } from '../../selectors/auth';
import { maybeEarlyRequest } from '../../utils/earlyRequests';
import { getFullHref } from '../../utils/history';
import { useParsedPathParams } from '../../utils/hooks/useParsedPathParams';
import { usePrevious } from 'ecosystem-marketplace-core/hooks/usePrevious';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
import { isSucceeded, isFulfilled } from 'ecosystem-marketplace-core/utils/requestState';
import { titlecase } from '../../utils/strings';
import { getPathPagination } from '../../utils/urls';
import StorefrontFiltersContext from '../../context/StorefrontFiltersContext';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
import CanonicalLink from '../../components/elements/CanonicalLink';
import GenericError from '../../components/error/GenericError';
import ResultsPage from '../../components/storefront/results/ResultsPage';
import { getIsUngatedForFeaturedApps } from '../../selectors/auth';
const EMPTY_SEARCH_RESULT = new SearchResult();
const EMPTY_ORDERED_SET = new Immutable.OrderedSet();

const FailedComponent = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "STOREFRONT_ERROR_LOAD",
  children: /*#__PURE__*/_jsx(GenericError, {})
});

const SearchResults = ({
  history,
  match: {
    params,
    url
  }
}) => {
  const {
    superCategory: activeSuperCategorySlug
  } = params;
  const isUngatedForFeaturedApps = useSelector(getIsUngatedForFeaturedApps);
  const {
    page: activePage = 1
  } = useParsedPathParams(params);
  const {
    trackInteraction,
    trackPageView,
    trackFilterView,
    trackResultClick
  } = useContext(StorefrontUsageTrackerContext);
  const {
    filters,
    clearFilters,
    isAllCategories,
    pushFilters,
    updateAllFilters,
    updateFilter
  } = useContext(StorefrontFiltersContext);
  const {
    [FilterKey.SUPER_CATEGORY]: activeSuperCategory,
    [FilterKey.CATEGORY]: activeCategory,
    [FilterKey.SEARCH]: searchValue
  } = filters;
  const [searchResult, setSearchResult] = useState(EMPTY_SEARCH_RESULT);
  const {
    total: totalResults
  } = searchResult;
  const [supplementalResults, setSupplementalResults] = useState(EMPTY_ORDERED_SET);
  const [shouldTrackPageView, setShouldTrackPageView] = useState(false);
  const [shouldTrackFilterView, setShouldTrackFilterView] = useState(false);
  const [requestState, setRequestState] = useState(UNINITIALIZED);
  const requestFulfilled = isFulfilled(requestState);
  const requestSucceeded = isSucceeded(requestState);
  const limit = DEFAULT_SEARCH_LENGTH;
  const [pageCount, setPageCount] = useState(0);
  const offset = (activePage - 1) * limit;
  const prevFilters = usePrevious(filters);
  const prevSearchValue = usePrevious(searchValue);
  const prevUrl = usePrevious(url);
  const getTrackedTotalResults = useCallback(() => {
    if (totalResults) {
      return totalResults;
    }

    if (searchValue && (activeCategory || activeSuperCategory)) {
      const {
        total: allCategoriesTotal
      } = supplementalResults.first(EMPTY_SEARCH_RESULT);
      return allCategoriesTotal;
    }

    return null;
  }, [activeCategory, activeSuperCategory, searchValue, supplementalResults, totalResults]);
  const handlePageChange = useCallback((page, _filters) => {
    const updatedFilters = _filters || filters;

    const _updatedFilters$toLoc = updatedFilters.toLocation(isUngatedForFeaturedApps),
          {
      pathname: newPathname
    } = _updatedFilters$toLoc,
          restLocation = _objectWithoutPropertiesLoose(_updatedFilters$toLoc, ["pathname"]);

    history.push(Object.assign({}, restLocation, {
      pathname: getPathPagination(newPathname, page)
    }));
    window.scrollTo(0, 0);
  }, [filters, history, isUngatedForFeaturedApps]);
  const handleSortChange = useCallback((field, order) => {
    const updatedFilters = filters.withMutations(mutableFilters => {
      mutableFilters.set(FilterKey.SORT_FIELD, field).set(FilterKey.SORT_ORDER, order);
    });
    pushFilters(updatedFilters);
  }, [filters, pushFilters]);
  const reduxDispatch = useDispatch();
  const handleResultClick = useCallback((result, index, count) => {
    trackResultClick(result, index, {
      totalResults: count
    });
    let referrer;

    if (activeSuperCategory) {
      referrer = `category::${activeSuperCategory}`;

      if (activeCategory) {
        referrer += `::${activeCategory}`;
      }
    }

    if (referrer) {
      reduxDispatch(setDetailsReferrer(referrer));
    }
  }, [activeCategory, activeSuperCategory, reduxDispatch, trackResultClick]);
  const resultProps = useMemo(() => {
    return {
      superCategory: activeSuperCategory
    };
  }, [activeSuperCategory]);
  const renderedResults = useMemo(() => {
    return /*#__PURE__*/_jsx(ResultsPage, {
      activePage: activePage,
      failedComponent: FailedComponent,
      filterable: true,
      filters: filters,
      offset: offset,
      onUpdateFilters: updateAllFilters,
      onClearFilters: clearFilters,
      onFilterChange: updateFilter,
      onPageChange: handlePageChange,
      onResultClick: handleResultClick,
      onSortChange: handleSortChange,
      requestState: requestState,
      resultProps: resultProps,
      searchResult: searchResult,
      sortable: true,
      supplementalResults: supplementalResults,
      trackInteraction: trackInteraction
    });
  }, [activePage, clearFilters, filters, handlePageChange, handleResultClick, handleSortChange, offset, requestState, resultProps, searchResult, supplementalResults, trackInteraction, updateAllFilters, updateFilter]);
  const socialImageUrl = useSocialScreenshotUrl();
  const renderedPageMeta = useMemo(() => {
    const canonicalUrl = url;
    let hasNextPage = false;
    let hasPrevPage = false;
    let noIndexFollow = false;
    let metaDescription;
    let statusCode;

    if (requestSucceeded) {
      hasNextPage = activePage < pageCount;
      hasPrevPage = activePage > 1;

      if (searchValue) {
        noIndexFollow = true;
      } else if (!totalResults) {
        noIndexFollow = true;
        statusCode = 404;
      } else if (totalResults < 3) {
        noIndexFollow = true;
      }
    }

    if (activeCategory) {
      metaDescription = unescapedText('appMarketplace.storefront.filters.category.metaDescription', {
        category: unescapedText(getCategoryI18nKey(activeCategory)).toLocaleLowerCase()
      });
    } else {
      metaDescription = unescapedText(`appMarketplace.storefront.filters.category.items.${(() => {
        switch (activeSuperCategory) {
          case SuperCategory.MARKETING:
            return 'marketing';

          case SuperCategory.SALES:
            return 'sales';

          case SuperCategory.CUSTOMER_SERVICE:
            return 'customerService';

          case SuperCategory.FINANCE:
            return 'finance';

          case SuperCategory.PRODUCTIVITY:
            return 'productivity';

          case SuperCategory.COMMERCE:
            return 'commerce';

          default:
            return 'all';
        }
      })()}.metaDescription`);
    }

    return /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsxs(DocumentHead, {
        children: [/*#__PURE__*/_jsx(CanonicalLink, {
          to: canonicalUrl
        }), hasPrevPage && /*#__PURE__*/_jsx(CanonicalLink, {
          rel: "prev",
          to: getPathPagination(url, activePage - 1)
        }), hasNextPage && /*#__PURE__*/_jsx(CanonicalLink, {
          rel: "next",
          to: getPathPagination(url, activePage + 1)
        }), /*#__PURE__*/_jsx(MetaDescription, {
          content: metaDescription
        }), noIndexFollow && /*#__PURE__*/_jsx(MetaRobots, {
          directive: ['nofollow', 'noindex']
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "card"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "site"
        }), /*#__PURE__*/_jsx(MetaTwitter, {
          name: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "type"
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "title",
          content: document.title
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "url",
          content: getFullHref(url)
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "description",
          content: metaDescription
        }), /*#__PURE__*/_jsx(MetaOpenGraph, {
          property: "image",
          content: socialImageUrl
        }), /*#__PURE__*/_jsx(PrerenderReady, {
          ready: requestFulfilled,
          statusCode: statusCode
        })]
      })
    });
  }, [activeCategory, activePage, activeSuperCategory, pageCount, requestFulfilled, requestSucceeded, searchValue, socialImageUrl, totalResults, url]);
  useEffect(() => {
    const count = totalResults || '';
    let documentTitle;

    if (activeCategory || activeSuperCategory) {
      documentTitle = I18n.text('appMarketplace.storefront.filters.category.htmlTitle', {
        count,
        category: titlecase(unescapedText(activeCategory ? getCategoryI18nKey(activeCategory) : getSuperCategoryI18nKey(activeSuperCategory)))
      });
    } else {
      documentTitle = I18n.text('appMarketplace.storefront.filters.category.items.all.htmlTitle', {
        count
      });
    }

    document.title = documentTitle;
  }, [activeCategory, activeSuperCategory, totalResults]);
  useEffect(() => {
    if (requestFulfilled && (shouldTrackPageView || shouldTrackFilterView)) {
      const trackedTotalResults = getTrackedTotalResults();
      const properties = {
        pageNumber: activePage,
        totalResults: trackedTotalResults !== null ? trackedTotalResults : undefined
      };

      if (shouldTrackPageView) {
        trackPageView(properties);
        setShouldTrackPageView(false);
      } else {
        trackFilterView(properties);
        setShouldTrackFilterView(false);
      }

      if (requestSucceeded && !trackedTotalResults) {
        trackInteraction('no results returned');
      }
    }
  }, [activePage, getTrackedTotalResults, requestFulfilled, requestSucceeded, shouldTrackFilterView, shouldTrackPageView, trackFilterView, trackInteraction, trackPageView]);
  useEffect(() => {
    if (url !== prevUrl || searchValue && searchValue !== prevSearchValue) {
      setShouldTrackPageView(true);
      setSearchResult(EMPTY_SEARCH_RESULT);
    }
  }, [prevSearchValue, prevUrl, searchValue, url]);
  useEffect(() => {
    if (url === prevUrl && searchValue === prevSearchValue && !filters.equals(prevFilters)) {
      setShouldTrackFilterView(true);
    }
  }, [filters, prevFilters, prevSearchValue, prevUrl, searchValue, url]);
  const language = getLang(); // TODO refactor

  useEffect(() => {
    const showPreferredLanguageFirst = filters.get(FilterKey.SHOW_PREFERRED_LANG_FIRST);
    const query = filters.toSearchQuery({
      length: limit,
      offset
    });
    let request;

    if (searchValue) {
      const supplementalFilterQueryMap = Immutable.OrderedMap().withMutations(mutableMap => {
        if (activeCategory || activeSuperCategory) {
          const allCategoriesFilters = filters.toAllCategories();
          mutableMap.set(allCategoriesFilters, allCategoriesFilters.toSearchQuery({
            length: limit,
            nextSearchMaxTrigger: 1
          }));
          const supplementalCategoryFilter = filters.withMutations(mutableFilters => mutableFilters.delete(FilterKey.CATEGORY_FEATURES).delete(FilterKey.SEARCH).set(FilterKey.SORT_FIELD, SortField.RANDOM));
          mutableMap.set(supplementalCategoryFilter, supplementalCategoryFilter.toSearchQuery({
            length: 3
          }));
        }
      });
      const primaryQuery = Object.assign({}, query, {
        nextSearchMaxTrigger: 1
      });
      const supplementalQueries = supplementalFilterQueryMap.valueSeq().toArray();
      request = ecosystemClient.searchApps({
        language,
        queries: [primaryQuery, ...supplementalQueries],
        showPreferredLanguageFirst
      }).then(response => {
        const [primary, ...supplementals] = response.results;
        return [primary, ...supplementalFilterQueryMap.mapEntries(([supplementalFilters], index) => {
          const supplementalResult = supplementals[index] || {};
          return [supplementalFilters, new SearchResult(Object.assign({}, supplementalResult, {
            filters: supplementalFilters
          }))];
        }).valueSeq().toArray()];
      });
    } else {
      let earlyRequestName; // TODO potentially support quick fetch for categories

      if (activeSuperCategorySlug) {
        earlyRequestName = `${activeSuperCategorySlug}-super-category`;
      }

      request = maybeEarlyRequest(earlyRequestName, () => {
        return ecosystemClient.searchApps({
          language,
          queries: [query],
          showPreferredLanguageFirst
        });
      }).then(response => response.results);
    }

    setRequestState(PENDING);
    request.then(([result, ...supplementals]) => {
      const newSearchResult = new SearchResult(result);
      setSearchResult(newSearchResult);
      setSupplementalResults(Immutable.OrderedSet(supplementals));
      setPageCount(Math.ceil(newSearchResult.total / limit));
      setRequestState(SUCCEEDED);
    }).catch(error => {
      console.error(error);
      setSearchResult(EMPTY_SEARCH_RESULT);
      setSupplementalResults(EMPTY_ORDERED_SET);
      setPageCount(0);
      setRequestState(FAILED);
    });
  }, [activeCategory, activeSuperCategory, activeSuperCategorySlug, filters, isAllCategories, limit, offset, language, searchValue]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [requestSucceeded && /*#__PURE__*/_jsx(NavMarker, {
      name: "STOREFRONT_LOAD"
    }), renderedResults, renderedPageMeta]
  });
};

SearchResults.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired
  }),
  match: PropTypes.shape({
    params: PropTypes.object.isRequired,
    url: PropTypes.string.isRequired
  }).isRequired
};
export default SearchResults;